import React, { useCallback, useEffect, useRef } from 'react';
import { cn } from './cn';
import { usePersistentState } from './usePersistentState';

/**
 * SplitView — รายการซ้าย รายละเอียดขวา เลื่อนแยกกัน ปรับความกว้างได้ และจำค่าไว้
 *
 * เหตุผลที่ต้องมี: หน้าตรวจคำขอต้องดู 100+ รายการต่อวัน
 * ถ้ากลับไปหน้ารายการทุกครั้ง จะเสียตำแหน่งในคิวและเสียเวลาโหลดใหม่
 *
 * ตัวคั่นปรับได้ทั้งเมาส์ (ลาก) และคีย์บอร์ด (ลูกศรซ้าย/ขวา, Home คืนค่าเริ่มต้น)
 */
export interface SplitViewProps {
  list: React.ReactNode;
  detail: React.ReactNode;
  /** คีย์สำหรับจำสัดส่วน เช่น 'claims-review' */
  persistKey: string;
  /** สัดส่วนเริ่มต้นของฝั่งซ้าย (0.2–0.7) */
  defaultRatio?: number;
  /** แสดงเมื่อยังไม่ได้เลือกรายการใด */
  detailPlaceholder?: React.ReactNode;
  hasDetail?: boolean;
  className?: string;
}

const MIN = 0.22;
const MAX = 0.62;

export const SplitView: React.FC<SplitViewProps> = ({
  list, detail, persistKey, defaultRatio = 0.38, detailPlaceholder, hasDetail = true, className,
}) => {
  const [ratio, setRatio] = usePersistentState<number>(`split.${persistKey}`, defaultRatio);
  const wrapRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const apply = useCallback((next: number) => setRatio(Math.min(MAX, Math.max(MIN, next))), [setRatio]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (!dragging.current || !wrapRef.current) return;
      const rect = wrapRef.current.getBoundingClientRect();
      apply((e.clientX - rect.left) / rect.width);
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [apply]);

  return (
    <div ref={wrapRef} className={cn('flex min-h-0 flex-1 overflow-hidden', className)}>
      <div
        className="flex min-h-0 min-w-[var(--split-list-min)] flex-col overflow-hidden border-r border-[var(--line-200)]"
        style={{ flexBasis: `${ratio * 100}%`, flexGrow: 0, flexShrink: 0 }}
      >
        {list}
      </div>

      <div
        role="separator"
        aria-orientation="vertical"
        aria-label="ปรับความกว้างของรายการ"
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={Math.round(MIN * 100)}
        aria-valuemax={Math.round(MAX * 100)}
        tabIndex={0}
        onPointerDown={() => {
          dragging.current = true;
          document.body.style.cursor = 'col-resize';
          document.body.style.userSelect = 'none';
        }}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); apply(ratio - 0.02); }
          if (e.key === 'ArrowRight') { e.preventDefault(); apply(ratio + 0.02); }
          if (e.key === 'Home') { e.preventDefault(); apply(defaultRatio); }
        }}
        className="w-[6px] shrink-0 cursor-col-resize bg-[var(--surface-100)] hover:bg-[var(--blue-200)] focus-visible:bg-[var(--blue-700)] focus-visible:outline-none"
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[var(--blue-050)]">
        {hasDetail ? detail : (
          <div className="flex flex-1 items-center justify-center p-[var(--space-10)]">{detailPlaceholder}</div>
        )}
      </div>
    </div>
  );
};
